import { useEffect, useRef } from "react";

import { measurePinchSpan, resolvePinchStep } from "@/shelf/utils/board-pinch";

import type { PinchSpan, ScreenPoint } from "@/shelf/utils/board-pinch";
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from "react";

/**
 * How far a press may travel and still be a click rather than a drag, in screen
 * pixels.
 *
 * Screen pixels rather than board millimeters, because it is a question about
 * the hand holding the pointer: at 25% zoom a millimeter is a third of a pixel,
 * and a threshold measured on the board would make every press at one end of
 * the zoom range a drag and none at the other.
 */
const PRESS_TRAVEL_THRESHOLD_PIXELS = 4;

/** How much of a wheel notch is one step of zoom. */
const WHEEL_ZOOM_SENSITIVITY = 0.0025;

interface Press {
  pointerId: number;
  isPanning: boolean;
  startXPixels: number;
  startYPixels: number;
  lastXPixels: number;
  lastYPixels: number;
}

/**
 * Moving the board by dragging it, zooming it with the wheel, and both at once
 * with two fingers.
 *
 * A press only **arms** the pan; it becomes one on the first move past the
 * threshold. That is what keeps every disc on the board clickable: a link that
 * stopped taking pointer events the moment a press landed on it is a link the
 * browser fires no click on, and the same press has to be able to end as either
 * a pan of the board or a visit to a project. A gesture that did move the board
 * opens nothing when it ends.
 *
 * Movement is measured from each pointer's own last position rather than read
 * off `movementX`, which Safari does not report for a finger - on an iPhone the
 * board did not move at all.
 *
 * The board takes every touch itself (`touch-none`), so the browser's own pinch
 * never reaches it: two fingers are a pinch here, zooming about the point
 * between them while the board follows them. Lifting one hands the board to the
 * other as a pan.
 *
 * Pointers are captured on the surface rather than tracked on the window, so a
 * drag that leaves the board still ends when it is let go.
 */
export const useBoardPan = ({
  panBy,
  surfaceRef,
  zoomAroundBy,
}: {
  panBy: (movement: { xPixels: number; yPixels: number }) => void;
  /** The board's own element, which the wheel listener is attached to directly. */
  surfaceRef: RefObject<HTMLElement | null>;
  zoomAroundBy: (options: { factor: number; xPixels: number; yPixels: number }) => void;
}) => {
  const press = useRef<Press | null>(null);
  // Every finger, pen or button down on the board, by pointer id.
  const pointers = useRef(new Map<number, ScreenPoint>());
  const pinch = useRef<PinchSpan | null>(null);
  // Whether the gesture in hand has moved the board, so the click or auxclick
  // it ends with is not also taken as opening the disc it ended on.
  const hasGestureMoved = useRef(false);

  const firstTwoPointers = () => {
    const [first, second] = [...pointers.current.values()];
    return first && second ? ([first, second] as const) : null;
  };

  const startPress = ({
    event,
    isPanning,
  }: {
    event: ReactPointerEvent<HTMLElement>;
    isPanning: boolean;
  }) => {
    press.current = {
      isPanning,
      pointerId: event.pointerId,
      startXPixels: event.clientX,
      startYPixels: event.clientY,
      lastXPixels: event.clientX,
      lastYPixels: event.clientY,
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    // The primary button, a finger or a pen, and the middle button. A secondary
    // press is the context menu's.
    if (event.button !== 0 && event.button !== 1) {
      return;
    }
    if (pointers.current.size === 0) {
      hasGestureMoved.current = false;
    }
    pointers.current.set(event.pointerId, { xPixels: event.clientX, yPixels: event.clientY });

    const pair = firstTwoPointers();
    if (pair) {
      // A second finger turns whatever the first was doing into a pinch.
      press.current = null;
      pinch.current = measurePinchSpan(pair);
      for (const pointerId of pointers.current.keys()) {
        event.currentTarget.setPointerCapture(pointerId);
      }
      return;
    }

    // The middle button always moves the board, from the first pixel: it can
    // never be a click on a disc, so there is nothing to wait for. Its default
    // is the browser's autoscroll, which would move the page instead.
    if (event.button === 1) {
      event.preventDefault();
      startPress({ event, isPanning: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    startPress({ event, isPanning: false });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) {
      return;
    }
    pointers.current.set(event.pointerId, { xPixels: event.clientX, yPixels: event.clientY });

    const pair = firstTwoPointers();
    const surface = surfaceRef.current;
    if (pinch.current && pair && surface) {
      const next = measurePinchSpan(pair);
      const step = resolvePinchStep({ previous: pinch.current, next });
      pinch.current = next;
      hasGestureMoved.current = true;
      const bounds = surface.getBoundingClientRect();
      zoomAroundBy({
        factor: step.factor,
        xPixels: step.about.xPixels - bounds.left,
        yPixels: step.about.yPixels - bounds.top,
      });
      panBy(step.pan);
      return;
    }

    const current = press.current;
    if (current?.pointerId !== event.pointerId) {
      return;
    }
    if (!current.isPanning) {
      const travelled =
        Math.abs(event.clientX - current.startXPixels) +
        Math.abs(event.clientY - current.startYPixels);
      if (travelled < PRESS_TRAVEL_THRESHOLD_PIXELS) {
        return;
      }
      current.isPanning = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const movement = {
      xPixels: event.clientX - current.lastXPixels,
      yPixels: event.clientY - current.lastYPixels,
    };
    current.lastXPixels = event.clientX;
    current.lastYPixels = event.clientY;
    if (movement.xPixels !== 0 || movement.yPixels !== 0) {
      hasGestureMoved.current = true;
    }
    panBy(movement);
  };

  const endPointer = (event: ReactPointerEvent<HTMLElement>) => {
    if (!pointers.current.delete(event.pointerId)) {
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (pinch.current) {
      pinch.current = null;
      // The finger left behind carries on as a pan, from where it is now.
      const [remainingId, remaining] = [...pointers.current.entries()][0] ?? [];
      press.current =
        remainingId === undefined || remaining === undefined
          ? null
          : {
              isPanning: true,
              pointerId: remainingId,
              startXPixels: remaining.xPixels,
              startYPixels: remaining.yPixels,
              lastXPixels: remaining.xPixels,
              lastYPixels: remaining.yPixels,
            };
      return;
    }
    if (press.current?.pointerId === event.pointerId) {
      press.current = null;
    }
  };

  // The middle button's autoscroll starts on the mouse press itself, which a
  // cancelled pointer press does not always stop - and once it starts, the
  // browser cancels the pointer and the drag with it.
  const onMouseDown = (event: ReactMouseEvent<HTMLElement>) => {
    if (event.button === 1) {
      event.preventDefault();
    }
  };

  // A gesture that moved the board ends there: the disc it was let go over is
  // not opened, in this tab or a new one.
  const onClickCapture = (event: ReactMouseEvent<HTMLElement>) => {
    if (hasGestureMoved.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * The wheel, attached natively and **non-passively** rather than through
   * React's `onWheel`: React registers `wheel` at the root as a passive
   * listener, where `preventDefault` does nothing — so the board would move and
   * the browser would scroll or zoom the page underneath it at the same time.
   * There is no way to opt one JSX handler out of that, which is why this is the
   * one listener here that is not a prop.
   *
   * A plain wheel zooms about the pointer — dragging is how the board moves —
   * and a held Ctrl or Command leaves it to the browser's own page zoom. The
   * board is the whole page, so there is nothing underneath it for a wheel to
   * scroll instead.
   */
  const zoomAroundByRef = useRef(zoomAroundBy);
  zoomAroundByRef.current = zoomAroundBy;

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      // With Ctrl or Command held the wheel is the browser's own page zoom, as
      // Ctrl+plus and Ctrl+minus are, and it is left to the browser.
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      event.preventDefault();
      // The middle button is also the wheel, and a hand pressing it to drag
      // rolls it a notch or two: while any button is held, the wheel does
      // nothing, or the board lurches in and out under the pan. Asked of the
      // event rather than of this hook's own press, which the browser can end
      // early - a cancelled pointer is still a button held down.
      if (event.buttons !== 0) {
        return;
      }
      const bounds = surface.getBoundingClientRect();
      zoomAroundByRef.current({
        // Exponential in the wheel's own units, so a notch is the same
        // proportion of the board at either end of the range rather than a
        // fixed number of percentage points — which is most of the range at 4%
        // and nothing at all at 400%.
        factor: Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY),
        xPixels: event.clientX - bounds.left,
        yPixels: event.clientY - bounds.top,
      });
    };
    surface.addEventListener("wheel", onWheel, { passive: false });
    return () => surface.removeEventListener("wheel", onWheel);
  }, [surfaceRef]);

  return {
    onAuxClickCapture: onClickCapture,
    onClickCapture,
    onMouseDown,
    onPointerCancel: endPointer,
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
  };
};
